import React, { useCallback, useEffect, useState } from "react";
import { flushSync } from "react-dom";
import { Handle, Position } from "reactflow";
import { Popover } from "antd";
import { PlusOutlined, ThunderboltOutlined } from "@ant-design/icons";
import { useWorkflowStore } from "../store";

export interface AddStepNodeData {
    insertIndex: number;
    parentStepId?: string;
    isEnabled?: boolean;
    closeSignal?: number;
}

const AddStepNodeComponent: React.FC<{ data: AddStepNodeData }> = ({ data }) => {
    const [open, setOpen] = useState(false);
    const addStep = useWorkflowStore((s) => s.addStep);
    const isEnabled = data.isEnabled ?? true;

    useEffect(() => {
        if (open) setOpen(false);
    }, [data.closeSignal]); // close popover when editor signals movement/pane-close

    const handleOpenChange = useCallback((next: boolean) => {
        if (!isEnabled) {
            flushSync(() => setOpen(false));
            return;
        }
        flushSync(() => setOpen(next));
    }, [isEnabled]);

    const handleSelect = (type: "condition" | "action") => {
        if (!isEnabled) return;
        setOpen(false);
        addStep(data.insertIndex, type);
    };

    const content = (
        <div className="add-step-popover" onClick={(e) => e.stopPropagation()}>
            <div
                className="add-step-popover__option"
                onClick={() => handleSelect("condition")}
                role="button"
                tabIndex={0}
                aria-label="Add condition step. Add a filter to check."
                onKeyDown={(e) => {
                    if (e.key === "Enter" || e.key === " ") {
                        e.preventDefault();
                        handleSelect("condition");
                    }
                }}
            >
                <div className="add-step-popover__icon">
                    <div className="automationIconsSprite condition-step-icon"></div>
                </div>
                <div className="add-step-popover__text">
                    <span className="add-step-popover__label">Condition</span>
                    <span className="add-step-popover__desc">Add a filter to check</span>
                </div>
            </div>
            <div
                className="add-step-popover__option"
                onClick={() => handleSelect("action")}
                role="button"
                tabIndex={0}
                aria-label="Add action step. Perform an action."
                onKeyDown={(e) => {
                    if (e.key === "Enter" || e.key === " ") {
                        e.preventDefault();
                        handleSelect("action");
                    }
                }}
            >
                <div className="add-step-popover__icon add-step-popover__icon--action">
                    <ThunderboltOutlined />
                </div>
                <div className="add-step-popover__text">
                    <span className="add-step-popover__label">Action</span>
                    <span className="add-step-popover__desc">Perform an action</span>
                </div>
            </div>
        </div>
    );

    return (
        <div className="add-step-node" onClick={(e) => e.stopPropagation()}>
            <Handle type="target" position={Position.Top} className="add-step-node__handle" />
            <Popover
                content={content}
                trigger="click"
                open={open}
                onOpenChange={handleOpenChange}
                placement="right"
                arrow
                overlayClassName="add-step-popover-overlay"
            >
                <button
                    type="button"
                    className={`add-step-node__btn nodrag nopan ${!isEnabled ? "add-step-node__btn--disabled" : ""}`}
                    aria-label="Add step. Add a condition or action."
                    disabled={!isEnabled}
                >
                    <span className={`add-step-node__icon ${open ? "add-step-node__icon--open" : ""}`}>
                        <PlusOutlined />
                    </span>
                </button>
            </Popover>
            <Handle type="source" position={Position.Bottom} className="add-step-node__handle" />
        </div>
    );
};

export const AddStepNode = React.memo(AddStepNodeComponent);
